import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List, Tooltip } from "@mui/material";
import RectangleIcon from "@mui/icons-material/Rectangle";
import moment from "moment";
import { useNavigate } from "react-router-dom";

// --- Interface Definitions ---

// This interface should match the structure of the processed job data from the parent page
interface ProcessedDataWarehouseJob {
    id: string;
    warehouseName: string;
    applicationName?: string;
    status: string;
    createdAt: string | null;
    actionedAt: string | null;
    ownerName: string | null | undefined;
    ownerAvatar?: string; // This can be used if you pass avatar URLs in the future
    approverName: string | null | undefined;
    approverAvatar?: string;
    reasonForRejection?: string;
}

interface DataWarehousesRequestsTableProps {
    jobs: ProcessedDataWarehouseJob[];
}

const DataWarehousesRequestsTable: React.FC<DataWarehousesRequestsTableProps> = ({
    jobs,
}) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    // Group statuses for consistent styling
    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const activeStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    return (
        <List sx={{ bgcolor: "#fafafa" }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px" }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        <Grid item xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Medium">WAREHOUSE NAME</Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Medium">APPLICATION</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Medium">REQUESTED BY</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Medium">REQUESTED ON</Typography>
                        </Grid>
                        <Grid textAlign="start" item xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Medium">ACTIONED BY / REASON</Typography>
                        </Grid>
                        <Grid item textAlign="start" xs={2}>
                            <Typography fontSize="12px" fontFamily="Gilroy-Medium">ACTIONED ON</Typography>
                        </Grid>
                    </Grid>
                </Box>
            </ListItem>
            {jobs.map((job: ProcessedDataWarehouseJob, index: number) => (
                <ListItem
                    key={job.id}
                    sx={{
                        border: cardHoverIndex === index ? "1px solid #000" : "1px solid #00000000",
                        mt: 1,
                        cursor: "pointer",
                        bgcolor: activeStatuses.includes(job.status)
                                ? "#FFFFFF"
                                : rejectedStatuses.includes(job.status)
                                ? "#F0443810"
                                : "#FDB02210", // Default to pending color
                        borderRadius: "8px",
                    }}
                    onClick={() => {
                        // Navigate to the data warehouse request details page
                        navigate(`/datasolutions/requests/data-warehouses/${job.id}`);
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => {
                        setCardHoverIndex(null);
                    }}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center" justifyContent="space-between">
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <RectangleIcon
                                        sx={{
                                            color: activeStatuses.includes(job.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(job.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            bgcolor: activeStatuses.includes(job.status)
                                                    ? "#12B76A"
                                                    : rejectedStatuses.includes(job.status)
                                                    ? "#F04438"
                                                    : "#FDB022",
                                            width: "4px",
                                            marginRight: "8px",
                                            height: "22px"
                                        }}
                                    />
                                    <Typography fontSize="14px" fontFamily="Gilroy-Medium">
                                        {job.warehouseName}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                    {job.applicationName || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Avatar
                                        src={job.ownerAvatar}
                                        alt={job.ownerName || ''}
                                        sx={{ marginRight: "8px", height: 30, width: 30 }}
                                    >
                                     {job.ownerName ? job.ownerName.charAt(0).toUpperCase() : ''}
                                    </Avatar>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {job.ownerName || 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                        {job.createdAt ? moment(job.createdAt).format("DD/MM/YYYY | hh:mm A") : 'N/A'}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                {job.status !== "pending" && (
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {job.approverName && (
                                        <Avatar
                                            alt={job.approverName}
                                            src={job.approverAvatar} // <-- ADD THIS LINE
                                            sx={{ marginRight: "8px", height: 30, width: 30 }}
                                        >
                                            {job.approverName.charAt(0).toUpperCase()}
                                        </Avatar>
                                        )}
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" mr={1}>
                                            {job.approverName || 'N/A'}
                                        </Typography>
                                        {rejectedStatuses.includes(job.status) && job.reasonForRejection && (
                                            <Tooltip title={`Reason: ${job.reasonForRejection}`}>
                                                <img src="/assets/info.svg" alt="info" />
                                            </Tooltip>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={2}>
                                {job.actionedAt && !pendingStatuses.includes(job.status) && (
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular">
                                            {moment(job.actionedAt).format("DD/MM/YYYY | hh:mm A")}
                                        </Typography>
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default DataWarehousesRequestsTable;